import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';
import { containerWrapper, standaloneWrapper } from '../components/Ui';
import { FilterContainer, FilterContainerMobile } from '../components/Filter';
import {
  additionalFiltersObj,
  createFilters,
  getDefaultParams,
  prepareFilterQueryParams,
  getRouteObjectFromURLSearchParams,
  getSearchParamsFromDynamicRoute,
  getParamStringFromUrlSearchParams,
  getSlugs,
  DEFAULT_THEME,
  DEFAULT_GENDERS,
  urlParams,
  isMobile,
  getGroupIds,
  isStandaloneProd,
  getDefaultTheme,
  useMutationObserver,
} from '../utils';

const Container = ({
  handlers: {
    fetchConfig,
    fetchThemeConfig,
    fetchContentFilter,
  },
  standalone,
  otherProps: {
    theme = DEFAULT_THEME,
    configId = urlParams.get('configId') || 2, // @TODO review default params
    urlFilters = { ...Object.fromEntries(urlParams || {}) },
    contentType = urlParams.get('contentType') || 'scene',
    onFilterChange,
    filtersData = {},
    additionalFilters = standalone && urlParams.get('marketplace') ? additionalFiltersObj : null,
    gendersOfModelsToShow = DEFAULT_GENDERS,
    showFirstNamesOnly = false,
    containerId = 'Filters-container',
    themeConfigId = urlParams.get('themeConfigId'),
  } = {},
}) => {
  const history = useHistory();
  const defaultFilters = getDefaultParams(contentType);
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [listId, setListId] = useState(null);
  const [themeConfig, setThemeConfig] = useState(null);
  const [contentFilter, setContentFilter] = useState(null);
  const [activeFilters, setActiveFilters] = useState(
    new URLSearchParams({
      ...defaultFilters,
      ...urlFilters,
    }),
  );
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isSortExpanded, setIsSortExpanded] = useState(false);
  const [isDateExpanded, setIsDateExpanded] = useState(false);
  const [hasFilterChanged, setHasFilterChanged] = useState(false);
  const hasFilterData = !isEmpty(filtersData);
  const ddFilteredObject = Object.entries(filtersData)
    .reduce((acc, [key, value]) => (Array.isArray(value) && value.length > 0
      ? { ...acc, [key]: value }
      : acc), {});

  useEffect(() => {
    if (!config) {
      fetchConfig(configId).then((response) => {
        if (response) {
          setConfig(response.find(({ id }) => id));
        }
      });
    }
    if (!themeConfigId) {
      setThemeConfig(getDefaultTheme(theme));
    } else {
      fetchThemeConfig(themeConfigId).then((response) => {
        if (response) {
          setThemeConfig(response.find(({ id }) => id));
        } else {
          setThemeConfig(getDefaultTheme(theme));
        }
      });
    }
  }, []);
  useEffect(() => {
    const nextActiveFilter = { ...Object.fromEntries(activeFilters) };
    setIsLoading(true);
    if (hasFilterData) {
      setContentFilter({
        ...ddFilteredObject,
      });
      setIsLoading(false);
    } else {
      fetchContentFilter(
        prepareFilterQueryParams({
          type: contentType,
          ...nextActiveFilter,
          ...getGroupIds(nextActiveFilter, additionalFilters),
        }),
      ).then((response) => {
        setIsLoading(false);
        if (response) {
          setContentFilter({
            ...response,
          });
        }
      });
    }
  }, [fetchContentFilter, JSON.stringify(filtersData), JSON.stringify({ ...Object.fromEntries(activeFilters) })]);

  const updateUrl = () => {
    const nextParams = getSearchParamsFromDynamicRoute();
    const nextFilter = new URLSearchParams(defaultFilters);
    nextParams.forEach((value, key) => {
      nextFilter.set(key, value);
    });
    setActiveFilters(nextFilter);
  };

  useMutationObserver(updateUrl);

  const handleFiltersToggle = () => {
    setIsFilterExpanded(!isFilterExpanded);
    setIsDateExpanded(false);
    setIsSortExpanded(false);
  };

  const handleSortToggle = () => {
    setIsSortExpanded(!isSortExpanded);
    setIsDateExpanded(false);
    setIsFilterExpanded(false);
  };

  const handleDateToggle = () => {
    setIsDateExpanded(!isDateExpanded);
    setIsSortExpanded(false);
    setIsFilterExpanded(false);
  };

  const handleClickOutside = (e) => {
    const container = document.getElementById(containerId);
    if (container && !container.contains(e.target)) {
      setIsFilterExpanded(false);
      setIsSortExpanded(false);
      setIsDateExpanded(false);
    }
  };

  const handleChangeRoute = (nextFilter, e) => {
    if (onFilterChange) {
      onFilterChange({
        ...getRouteObjectFromURLSearchParams(nextFilter, defaultFilters),
        slugs: getSlugs(nextFilter, contentFilter),
      })(e);
    } else {
      history.push(
        `/${isStandaloneProd() ? 'index.html' : ''}?${getParamStringFromUrlSearchParams(nextFilter, defaultFilters)}`,
      );
    }
  };

  const handleReset = (e) => {
    const urlParamsSearch = new URLSearchParams(window.location.search);
    const searchQuery = urlParamsSearch.get('q');
    const defaultFilterParams = hasFilterData && searchQuery
      ? { ...defaultFilters, q: searchQuery, source: urlFilters.source } : defaultFilters;
    const nextFilter = new URLSearchParams(defaultFilterParams);
    handleChangeRoute(nextFilter, e);
    setIsFilterExpanded(false);
    setIsSortExpanded(false);
    setIsDateExpanded(false);
    setActiveFilters(nextFilter);
  };

  const handleOnChangeFilter = (key, value, applyFilters = false) => (e) => {
    const nextFilter = new URLSearchParams(activeFilters);
    if (value?.length) {
      nextFilter.set(key, value);
    } else {
      nextFilter.delete(key);
    }

    if (applyFilters || config?.enableDynamicSearch || ['date', 'sortby'].includes(key)
      || hasFilterData) {
      handleChangeRoute(nextFilter, e);
    }

    setHasFilterChanged(!applyFilters);
    setIsFilterExpanded(!applyFilters && !config?.enableDynamicSearch && !['date', 'sortby'].includes(key));
    setIsSortExpanded(false);
    setIsDateExpanded(false);
    setActiveFilters(nextFilter);
  };

  useEffect(() => {
    if (!isMobile()) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    Array.from(document.getElementsByTagName('section')).forEach((section) => {
      if (String(section.id).startsWith('List-container')) {
        setListId(section.id);
      }
    });

    return () => {
      if (!isMobile()) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, []);

  const filters = createFilters(
    config,
    activeFilters,
    contentFilter,
    defaultFilters,
    additionalFilters,
    gendersOfModelsToShow,
    showFirstNamesOnly,
    hasFilterData,
  );
  const ContainerWrapper = isMobile() ? FilterContainerMobile : FilterContainer;
  return (
    <div
      role="dialog"
      aria-label="Filter Videos"
      aria-controls={listId}
      className={standalone
        ? standaloneWrapper({ ...theme?.color, isMobile: isMobile() })
        : containerWrapper}
    >
      {!isEmpty(filters) && !isEmpty(config) && !isEmpty(themeConfig) && (
        <ContainerWrapper
          isFilterExpanded={isFilterExpanded}
          filters={filters}
          isLoading={isLoading}
          config={config}
          filtersData={filtersData}
          onReset={handleReset}
          isSortExpanded={isSortExpanded}
          isDateExpanded={isDateExpanded}
          onSortToggle={handleSortToggle}
          onDateToggle={handleDateToggle}
          onFilterChange={handleOnChangeFilter}
          hasFilterChanged={hasFilterChanged}
          onFiltersToggle={handleFiltersToggle}
          themeConfig={themeConfig}
        />
      )}
    </div>
  );
};

Container.propTypes = {
  standalone: PropTypes.bool,
  otherProps: PropTypes.object,
  handlers: PropTypes.object,
};

export default Container;
